import { Switch, Route, Redirect, useLocation } from "wouter";
import { lazy, Suspense } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { PublicLayout } from "@/components/public-layout";
import { useAuth } from "@/hooks/use-auth";
import { useSiteMetadata } from "@/hooks/use-site-metadata";
import { usePageTracking } from "@/hooks/use-page-tracking";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";

import LandingPage from "@/pages/landing";
// Keep admin editors, reports, payment tools, and maps out of the homepage download.
const AppSidebar = lazy(() => import("@/components/app-sidebar").then(m => ({ default: m.AppSidebar })));
const MembershipPage = lazy(() => import("@/pages/membership"));
const MembershipApplyPage = lazy(() => import("@/pages/membership-apply"));
const CmsPageView = lazy(() => import("@/pages/cms-page"));
const SitemapPage = lazy(() => import("@/pages/sitemap"));
const DashboardPage = lazy(() => import("@/pages/dashboard"));
const InstitutionsPage = lazy(() => import("@/pages/institutions"));
const InstitutionDetailPage = lazy(() => import("@/pages/institution-detail"));
const DirectoryPage = lazy(() => import("@/pages/directory"));
const MembershipsPage = lazy(() => import("@/pages/memberships"));
const RegisterPage = lazy(() => import("@/pages/register"));
const PaymentsPage = lazy(() => import("@/pages/payments"));
const ReportsPage = lazy(() => import("@/pages/reports"));
const NotificationsPage = lazy(() => import("@/pages/notifications"));
const SettingsPage = lazy(() => import("@/pages/settings"));
const ContentManagerPage = lazy(() => import("@/pages/content-manager"));
const MenuManagerPage = lazy(() => import("@/pages/menu-manager"));
const ApplicationsPage = lazy(() => import("@/pages/applications"));
const AdminEventsPage = lazy(() => import("@/pages/admin-events"));
const AdminWebinarsPage = lazy(() => import("@/pages/admin-webinars"));
const AdminRegionalEventsPage = lazy(() => import("@/pages/admin-regional-events"));
const AdminFaqsPage = lazy(() => import("@/pages/admin-faqs"));
const AdminArticlesPage = lazy(() => import("@/pages/admin-articles"));
const AdminListservPage = lazy(() => import("@/pages/admin-listserv"));
const AdminEmailLogPage = lazy(() => import("@/pages/admin-email-log"));
const AdminStaffArchivePage = lazy(() => import("@/pages/admin-staff-archive"));
const AdminUsersPage = lazy(() => import("@/pages/admin-users"));
const MemberDashboard = lazy(() => import("@/pages/member-dashboard"));
const AuthLoginPage = lazy(() => import("@/pages/auth-login"));
const AuthForgotPasswordPage = lazy(() => import("@/pages/auth-forgot-password"));
const AuthResetPasswordPage = lazy(() => import("@/pages/auth-reset-password"));
const EventsCalendarPage = lazy(() => import("@/pages/events-calendar"));
const EventDetailPage = lazy(() => import("@/pages/event-detail"));
const WebinarsPage = lazy(() => import("@/pages/webinars"));
const WebinarDetailPage = lazy(() => import("@/pages/webinar-detail"));
const RegionalEventsPage = lazy(() => import("@/pages/regional-events"));
const FaqsPage = lazy(() => import("@/pages/faqs-page"));
const ArticlesPage = lazy(() => import("@/pages/articles"));
const ArticleDetailPage = lazy(() => import("@/pages/article-detail"));
const SearchResultsPage = lazy(() => import("@/pages/search-results"));
const MentorsDirectoryPage = lazy(() => import("@/pages/mentors-directory"));
const MemberMapPage = lazy(() => import("@/pages/member-map"));
const AdminDocumentsPage = lazy(() => import("@/pages/admin-documents"));
const NotFound = lazy(() => import("@/pages/not-found"));

function AdminLayout({ children }: { children: React.ReactNode }) {
  const { logout } = useAuth();
  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <AppSidebar />
        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex h-14 items-center justify-between gap-4 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4">
            <SidebarTrigger data-testid="button-sidebar-toggle" />
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => logout()}
                data-testid="button-logout-header"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </header>
          <main className="flex-1 overflow-auto p-6">{children}</main>
        </div>
      </div>
    </SidebarProvider>
  );
}

function AdminRoutes() {
  return (
    <AdminLayout>
      <Switch>
        <Route path="/admin" component={DashboardPage} />
        <Route path="/admin/dashboard" component={DashboardPage} />
        <Route path="/admin/institutions" component={InstitutionsPage} />
        <Route path="/admin/institutions/:id" component={InstitutionDetailPage} />
        <Route path="/admin/directory" component={DirectoryPage} />
        <Route path="/admin/memberships" component={MembershipsPage} />
        <Route path="/admin/register" component={RegisterPage} />
        <Route path="/admin/payments" component={PaymentsPage} />
        <Route path="/admin/reports" component={ReportsPage} />
        <Route path="/admin/notifications" component={NotificationsPage} />
        <Route path="/admin/email-log" component={AdminEmailLogPage} />
        <Route path="/admin/staff-archive" component={AdminStaffArchivePage} />
        <Route path="/admin/applications" component={ApplicationsPage} />
        <Route path="/admin/events" component={AdminEventsPage} />
        <Route path="/admin/webinars" component={AdminWebinarsPage} />
        <Route path="/admin/regional-events" component={AdminRegionalEventsPage} />
        <Route path="/admin/faqs" component={AdminFaqsPage} />
        <Route path="/admin/articles" component={AdminArticlesPage} />
        <Route path="/admin/listserv" component={AdminListservPage} />
        <Route path="/admin/documents" component={AdminDocumentsPage} />
        <Route path="/admin/users" component={AdminUsersPage} />
        <Route path="/admin/content" component={ContentManagerPage} />
        <Route path="/admin/nav" component={MenuManagerPage} />
        <Route path="/admin/settings" component={SettingsPage} />
        <Route component={NotFound} />
      </Switch>
    </AdminLayout>
  );
}

function MemberRoutes() {
  return (
    <PublicLayout>
      <Switch>
        <Route path="/member" component={MemberDashboard} />
        <Route component={NotFound} />
      </Switch>
    </PublicLayout>
  );
}

function PublicRoutes() {
  return (
    <PublicLayout>
      <Switch>
        <Route path="/" component={LandingPage} />
        <Route path="/membership/apply" component={MembershipApplyPage} />
        <Route path="/privacy" component={CmsPageView} />
        <Route path="/membership/map" component={MemberMapPage} />
        <Route path="/membership/general-meeting" component={CmsPageView} />
        <Route path="/membership" component={MembershipPage} />
        <Route path="/about/news/:slug" component={ArticleDetailPage} />
        <Route path="/about/news" component={ArticlesPage} />
        <Route path="/about/:slug" component={CmsPageView} />
        <Route path="/resources/trauma-faqs" component={FaqsPage} />
        <Route path="/prevention/injury-prevention-tips/:slug" component={ArticleDetailPage} />
        <Route path="/prevention/injury-prevention-tips" component={ArticlesPage} />
        <Route path="/resources/injury-prevention-tips/:slug" component={ArticleDetailPage} />
        <Route path="/resources/injury-prevention-tips" component={ArticlesPage} />
        <Route path="/resources/:slug" component={CmsPageView} />
        <Route path="/education/webinars/:slug" component={WebinarDetailPage} />
        <Route path="/education/webinars" component={WebinarsPage} />
        <Route path="/education/regional-conferences" component={RegionalEventsPage} />
        <Route path="/education/:slug" component={CmsPageView} />
        <Route path="/prevention/:slug" component={CmsPageView} />
        <Route path="/legislative/:slug" component={CmsPageView} />
        <Route path="/members/networking" component={MentorsDirectoryPage} />
        <Route path="/members/mentors">
          <Redirect to="/members/networking" replace />
        </Route>
        <Route path="/members/:slug" component={CmsPageView} />
        <Route path="/events/:id" component={EventDetailPage} />
        <Route path="/events" component={EventsCalendarPage} />
        <Route path="/directory" component={DirectoryPage} />
        <Route path="/search" component={SearchResultsPage} />
        <Route path="/sitemap" component={SitemapPage} />
        <Route path="*" component={CmsPageView} />
        <Route component={NotFound} />
      </Switch>
    </PublicLayout>
  );
}

function Router() {
  useSiteMetadata();
  usePageTracking();
  const { user, isLoading } = useAuth();
  const [location] = useLocation();

  if (isLoading && /^\/(?:admin|member)(?:\/|$)/.test(location)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (location.startsWith("/admin")) {
    return user?.isAdmin ? <AdminRoutes /> : <AuthLoginPage />;
  }

  if (location === "/member" || location.startsWith("/member/")) {
    return user ? (
      <MemberRoutes />
    ) : (
      <PublicLayout>
        <AuthLoginPage />
      </PublicLayout>
    );
  }

  return (
    <Switch>
      <Route path="/auth/register">
        <Redirect to="/forgot-password" />
      </Route>
      <Route path="/auth/login">
        <PublicLayout><AuthLoginPage /></PublicLayout>
      </Route>
      <Route path="/forgot-password">
        <PublicLayout><AuthForgotPasswordPage /></PublicLayout>
      </Route>
      <Route path="/reset-password">
        <PublicLayout><AuthResetPasswordPage /></PublicLayout>
      </Route>
      <Route>
        <PublicRoutes />
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={<div role="status" className="flex min-h-screen items-center justify-center">Loading page…</div>}>
          <Router />
        </Suspense>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
